import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { LuCircleAlert, LuCircleCheckBig } from 'react-icons/lu';

import { BillingGroupRun, RunStatus } from '@ndvr/billing-api';

import { FilterType } from '@/cip/_openapi/portfolio-optimizer';
import { HintLabel } from '@/components/HintLabel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { EllipsisDropdownMenuTrigger } from '@/components/ui/dropdown-menu-triggers';
import { formatCurrency, formatCurrencyShort } from 'utils';

import { useBillingRunReportPreview, useGenerateBillingRunReport } from './billing-run-queries';
import { BillingRunSelectionColumnOptions, createBillingRunSelectionColumn } from './billing-run-selection-column';
import {
    amendableFeeTypes,
    BillingRunTableRow,
    cashEncumberedForRow,
    cashShortfallForRow,
    currentPeriodFee,
    excludedAum,
    parseAmount,
    primaryLabel,
    runFailed,
    SETTLEMENT_FILTER_OPTIONS,
    SETTLEMENT_STATUS_CLASSNAME,
    SETTLEMENT_STATUS_LABEL,
    SettlementStatus,
    settlementStatusForRow,
    totalAum,
    totalOutstanding,
} from './billing-run-table';
import { BillingRunNameCell } from './BillingRunNameCell';
import { InvoicePreviewDialog } from './InvoicePreviewDialog';

const InvoiceButton = ({ run }: { run: BillingGroupRun }) => {
    const previewReport = useBillingRunReportPreview();
    const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    return (
        <>
            <Button
                variant="outline2"
                size="sm"
                className="px-2 min-[1800px]:px-3"
                disabled={previewReport.isPending}
                onClick={() =>
                    previewReport.mutate(run.billingGroupRunGUID, {
                        onSuccess: (blob) => {
                            setPreviewBlob(blob);
                            setPreviewOpen(true);
                        },
                    })
                }
            >
                {previewReport.isPending && (
                    <span className="mr-2 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                )}
                Invoice
            </Button>
            <InvoicePreviewDialog
                blob={previewBlob}
                fileName={`${run.billingDate.replace(/Z$/, '')}-Claro_Client_Billing_Report.pdf`}
                open={previewOpen}
                onOpenChange={setPreviewOpen}
            />
        </>
    );
};

const DownloadInvoiceMenuItem = ({ run }: { run: BillingGroupRun }) => {
    const generateReport = useGenerateBillingRunReport();
    return (
        <DropdownMenuItem
            disabled={generateReport.isPending}
            onClick={() => generateReport.mutate(run.billingGroupRunGUID)}
        >
            Download invoice
        </DropdownMenuItem>
    );
};

type UseBillingRunColumnsParams = BillingRunSelectionColumnOptions & {
    accountNameByGUID: Map<string, string>;
    isPastBillingDate: boolean;
    isUpcomingBillingDate: boolean;
    onViewHistory: (run: BillingGroupRun) => void;
    onAmendFees: (run: BillingGroupRun) => void;
};

export const useBillingRunColumns = ({
    accountNameByGUID,
    isPastBillingDate,
    isUpcomingBillingDate,
    selectedRunGUIDs,
    getSelectionBlockReason,
    onRunsSelectionChange,
    onViewHistory,
    onAmendFees,
}: UseBillingRunColumnsParams): ColumnDef<BillingRunTableRow>[] =>
    useMemo(() => {
        const allColumns: ColumnDef<BillingRunTableRow>[] = [
            createBillingRunSelectionColumn({
                selectedRunGUIDs,
                getSelectionBlockReason,
                onRunsSelectionChange,
            }),
            {
                id: 'billingGroupName',
                accessorFn: primaryLabel,
                header: 'Billing Group',
                sortingFn: (a, b, id) =>
                    String(a.getValue(id) ?? '').localeCompare(String(b.getValue(id) ?? ''), undefined, {
                        sensitivity: 'base',
                    }),
                cell: ({ row }) => (
                    <BillingRunNameCell
                        row={row}
                        accountNameByGUID={accountNameByGUID}
                        isUpcomingBillingDate={isUpcomingBillingDate}
                    />
                ),
                meta: {
                    cellClassName: 'border-r border-gray-200',
                    headerClassName: 'border-r border-gray-200 bg-gray-50',
                },
            },
            {
                id: 'billableNav',
                accessorFn: (row) => (row.kind === 'detail' ? undefined : parseAmount(row.rollup.billableAum)),
                header: 'Billable NAV',
                cell: ({ row, getValue }) => {
                    if (isUpcomingBillingDate && runFailed(row.original)) return <span>—</span>;
                    const value = getValue<number | undefined>();
                    if (value == null) return null;
                    const total = totalAum(row.original);
                    const excluded = excludedAum(row.original);
                    if (total == null || excluded == null) return formatCurrencyShort(value);
                    return (
                        <HintLabel
                            hint={
                                <div className="space-y-0.5">
                                    <div>Total AUM: {formatCurrencyShort(total)}</div>
                                    <div>Excluded AUM: {formatCurrencyShort(excluded)}</div>
                                </div>
                            }
                        >
                            {formatCurrencyShort(value)}
                        </HintLabel>
                    );
                },
                meta: {
                    cellClassName: 'text-right whitespace-nowrap border-r border-gray-200',
                    headerClassName: 'text-right border-r border-gray-200 bg-gray-50',
                },
            },
            {
                id: 'currentPeriodFee',
                accessorFn: currentPeriodFee,
                header: isUpcomingBillingDate ? 'Estimated Fee' : 'Current Period Fee',
                cell: ({ row, getValue }) => {
                    if (isUpcomingBillingDate && runFailed(row.original)) {
                        return <span className="text-muted-foreground text-sm">Estimate unavailable</span>;
                    }
                    const value = getValue<number>();
                    return (
                        <span
                            className={row.original.kind === 'detail' && value < 0 ? 'font-semibold text-red-900' : ''}
                        >
                            {formatCurrency(value)}
                        </span>
                    );
                },
                meta: {
                    cellClassName: 'text-right whitespace-nowrap border-r border-gray-200',
                    headerClassName: 'text-right border-r border-gray-200 bg-gray-50',
                },
            },
            {
                id: 'totalOutstandingFee',
                accessorFn: totalOutstanding,
                header: 'Total Outstanding Fee',
                cell: ({ getValue }) => {
                    const value = getValue<number | undefined>();
                    return value == null ? null : formatCurrency(value);
                },
                meta: {
                    cellClassName: 'text-right whitespace-nowrap border-r border-gray-200',
                    headerClassName: 'text-right border-r border-gray-200 bg-gray-50',
                },
            },
            {
                id: 'cashEncumbered',
                accessorFn: cashEncumberedForRow,
                header: 'Cash Encumbered',
                cell: ({ row, getValue }) => {
                    const cash = getValue<number | undefined>();
                    if (cash == null) return row.original.kind === 'detail' ? null : <span>—</span>;
                    const shortfall = cashShortfallForRow(row.original);
                    if (shortfall == null) return <span>—</span>;
                    return (
                        <div className="flex flex-col items-end gap-1">
                            <span>{formatCurrency(cash)}</span>
                            <span
                                className={`flex items-center gap-1 text-xs font-medium ${
                                    shortfall === 0 ? 'text-green-700' : 'text-red-900'
                                }`}
                            >
                                {shortfall === 0 ? (
                                    <LuCircleCheckBig className="h-3.5 w-3.5" />
                                ) : (
                                    <LuCircleAlert className="h-3.5 w-3.5" />
                                )}
                                {shortfall === 0 ? 'Covered' : `Short ${formatCurrency(shortfall)}`}
                            </span>
                        </div>
                    );
                },
                meta: {
                    cellClassName: 'text-right whitespace-nowrap border-r border-gray-200',
                    headerClassName: 'text-right border-r border-gray-200 bg-gray-50',
                },
            },
            {
                id: 'settlementStatus',
                accessorFn: settlementStatusForRow,
                header: 'Settlement Status',
                cell: ({ row, getValue }) => {
                    if (row.original.kind === 'detail') return null;
                    const status = getValue<SettlementStatus>();
                    return (
                        <Badge variant="outline" className={SETTLEMENT_STATUS_CLASSNAME[status]}>
                            {SETTLEMENT_STATUS_LABEL[status]}
                        </Badge>
                    );
                },
                meta: {
                    cellClassName: 'whitespace-nowrap border-r border-gray-200',
                    headerClassName: 'border-r border-gray-200 bg-gray-50',
                    filterType: FilterType.LIST,
                    filterOptionsFn: () => SETTLEMENT_FILTER_OPTIONS,
                    formatOption: (v: string) => SETTLEMENT_STATUS_LABEL[v as SettlementStatus],
                },
            },
            {
                id: 'actions',
                enableResizing: false,
                enableSorting: false,
                header: '',
                cell: ({ row }) =>
                    row.original.kind === 'group' && !isUpcomingBillingDate ? (
                        <div className="flex items-center justify-end gap-1 min-[1800px]:gap-2">
                            <InvoiceButton run={row.original.run} />
                            <DropdownMenu>
                                <EllipsisDropdownMenuTrigger />
                                <DropdownMenuContent align="end">
                                    {row.original.run.runStatus === RunStatus.COMPLETED &&
                                        amendableFeeTypes(row.original.run).length > 0 && (
                                            <DropdownMenuItem onClick={() => onAmendFees(row.original.run)}>
                                                Amend fees
                                            </DropdownMenuItem>
                                        )}
                                    <DropdownMenuItem onClick={() => onViewHistory(row.original.run)}>
                                        View history
                                    </DropdownMenuItem>
                                    <DownloadInvoiceMenuItem run={row.original.run} />
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    ) : null,
                meta: {
                    cellClassName: 'whitespace-nowrap',
                    headerClassName: 'bg-gray-50',
                },
            },
        ];

        if (isUpcomingBillingDate) {
            const upcomingColumnIds = new Set(['billingGroupName', 'billableNav', 'currentPeriodFee']);
            return allColumns.filter((column) => column.id != null && upcomingColumnIds.has(column.id));
        }

        return isPastBillingDate
            ? allColumns.filter((column) => column.id !== 'totalOutstandingFee' && column.id !== 'cashEncumbered')
            : allColumns;
    }, [
        accountNameByGUID,
        getSelectionBlockReason,
        isPastBillingDate,
        isUpcomingBillingDate,
        onRunsSelectionChange,
        onViewHistory,
        onAmendFees,
        selectedRunGUIDs,
    ]);
